import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import MultiImageUpload from "@/components/MultiImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, Loader2, Tag, ExternalLink, FileText, MousePointerClick, X } from "lucide-react";
import { toast } from "sonner";

const emptyOffer = {
  title: "", description: "", image_path: "", images: [], category_id: "", price: "", price_value: "", discount: "",
  badge: "", featured: false, sold_out: false, link_type: "internal", external_url: "", terms: "", active: true, expires_at: "",
};

const isExpired = (o) => o.expires_at && new Date(o.expires_at) < new Date();

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOffer);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [hasBusiness, setHasBusiness] = useState(true);

  const load = async () => {
    const [o, c, b] = await Promise.all([api.get("/offers"), api.get("/categories"), api.get("/business/me")]);
    setOffers(o.data);
    setCategories(c.data);
    setHasBusiness(!!b.data);
  };

  useEffect(() => { (async () => { try { await load(); } finally { setLoading(false); } })(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const openNew = () => { setEditing(null); setForm(emptyOffer); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...emptyOffer, ...o, images: o.images?.length ? o.images : (o.image_path ? [o.image_path] : []), expires_at: o.expires_at ? o.expires_at.slice(0, 10) : "" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.link_type === "external" && !form.external_url.trim()) return toast.error("External URL required");
    setSaving(true);
    const payload = { ...form, price_value: form.price_value === "" || form.price_value == null ? null : Number(form.price_value), image_path: form.images[0] || "", expires_at: form.expires_at || null };
    try {
      if (editing) await api.put(`/offers/${editing.offer_id}`, payload);
      else await api.post("/offers", payload);
      await load();
      setOpen(false);
      toast.success(editing ? "Offer updated" : "Offer created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/offers/${id}`);
    await load();
    toast.success("Offer deleted");
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat });
      setNewCat("");
      const c = await api.get("/categories");
      setCategories(c.data);
      toast.success("Category added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const removeCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    await load();
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></div>);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between">
          <div>
            <span className="overline text-primary">Deals</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Offers & discounts</h1>
          </div>
          <Button onClick={openNew} disabled={!hasBusiness} className="rounded-full bg-primary hover:bg-secondary" data-testid="add-offer-btn">
            <Plus className="w-4 h-4 mr-1" /> New offer
          </Button>
        </div>

        {!hasBusiness && <p className="mt-6 text-sm text-primary" data-testid="offers-no-business">Create your business profile first to add offers.</p>}

        {/* Categories */}
        <div className="mt-6 bg-card border border-white/10 rounded-xl p-5">
          <h3 className="font-display font-semibold text-sm">Categories</h3>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {categories.map((c) => (
              <span key={c.category_id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/40 text-sm" data-testid={`category-${c.category_id}`}>
                {c.name}
                <button onClick={() => removeCategory(c.category_id)} data-testid={`del-category-${c.category_id}`}><X className="w-3 h-3 hover:text-primary" /></button>
              </span>
            ))}
            <div className="flex gap-2 items-center">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Add category" data-testid="new-category-input" className="h-8 w-40 bg-background border-white/10" onKeyDown={(e) => e.key === "Enter" && addCategory()} />
              <Button size="sm" variant="outline" onClick={addCategory} className="rounded-full border-white/15 h-8" data-testid="add-category-btn"><Plus className="w-3 h-3" /></Button>
            </div>
          </div>
        </div>

        {/* Offers grid */}
        {offers.length === 0 ? (
          <div className="mt-8 text-center py-16 bg-card border border-white/10 rounded-xl" data-testid="offers-empty">
            <Tag className="w-10 h-10 text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">No offers yet. Create your first deal.</p>
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((o) => (
              <div key={o.offer_id} className="bg-card border border-white/10 rounded-xl overflow-hidden group" data-testid={`offer-card-${o.offer_id}`}>
                <div className="aspect-video bg-secondary/20 relative">
                  {o.image_path ? <img src={fileUrl(o.image_path)} alt={o.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Tag className="w-8 h-8 text-primary/40" /></div>}
                  {o.badge && <Badge className="absolute top-2 left-2 bg-primary">{o.badge}</Badge>}
                  {!o.active && <Badge className="absolute top-2 right-2 bg-muted">Hidden</Badge>}
                  {o.active && isExpired(o) && <Badge className="absolute top-2 right-2 bg-secondary" data-testid={`offer-expired-${o.offer_id}`}>Expired</Badge>}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-display font-semibold">{o.title}</h4>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><MousePointerClick className="w-3 h-3" />{o.clicks || 0}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm">
                    {o.discount && <span className="text-primary font-semibold">{o.discount}</span>}
                    {o.price && <span className="text-muted-foreground">{o.price}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{o.description}</p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                    {o.link_type === "external" ? <ExternalLink className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                    {o.link_type === "external" ? "External link" : "Detail page"}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => openEdit(o)} className="rounded-full border-white/15 flex-1" data-testid={`edit-offer-${o.offer_id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(o.offer_id)} className="text-primary" data-testid={`delete-offer-${o.offer_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit offer" : "New offer"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <MultiImageUpload label="Offer images" value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} testid="upload-offer-image" />
            <div><Label className="overline text-muted-foreground">Title *</Label><Input value={form.title} onChange={set("title")} placeholder="50% off all coffees" data-testid="offer-title" className="mt-2 bg-background border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Discount</Label><Input value={form.discount} onChange={set("discount")} placeholder="50% OFF" data-testid="offer-discount" className="mt-2 bg-background border-white/10" /></div>
              <div><Label className="overline text-muted-foreground">Price (PKR number)</Label><Input type="number" min="0" value={form.price_value ?? ""} onChange={set("price_value")} placeholder="450" data-testid="offer-price-value" className="mt-2 bg-background border-white/10" /></div>
              <div><Label className="overline text-muted-foreground">Price text (optional)</Label><Input value={form.price} onChange={set("price")} placeholder="Rs 4,999" data-testid="offer-price" className="mt-2 bg-background border-white/10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-muted-foreground">Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-2 bg-background border-white/10" data-testid="offer-category"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="none">None</SelectItem>
                    {categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="overline text-muted-foreground">Badge</Label><Input value={form.badge} onChange={set("badge")} placeholder="Hot / New" data-testid="offer-badge" className="mt-2 bg-background border-white/10" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">Expires (optional)</Label><Input type="date" value={form.expires_at} onChange={set("expires_at")} data-testid="offer-expires" className="mt-2 bg-background border-white/10" /></div>
            <div><Label className="overline text-muted-foreground">Description</Label><Textarea value={form.description} onChange={set("description")} data-testid="offer-description" className="mt-2 bg-background border-white/10" /></div>
            <div>
              <Label className="overline text-muted-foreground">When clicked, go to</Label>
              <Select value={form.link_type} onValueChange={(v) => setForm((f) => ({ ...f, link_type: v }))}>
                <SelectTrigger className="mt-2 bg-background border-white/10" data-testid="offer-linktype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="internal">Detail page (inside DealWisp)</SelectItem>
                  <SelectItem value="external">External URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.link_type === "external" ? (
              <div><Label className="overline text-muted-foreground">External URL *</Label><Input value={form.external_url} onChange={set("external_url")} placeholder="https://..." data-testid="offer-external-url" className="mt-2 bg-background border-white/10" /></div>
            ) : (
              <div><Label className="overline text-muted-foreground">Terms & details</Label><Textarea value={form.terms} onChange={set("terms")} placeholder="Valid till... conditions..." data-testid="offer-terms" className="mt-2 bg-background border-white/10" /></div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm">Featured (pinned to top)</span>
              <Switch checked={!!form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="offer-featured" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Sold out</span>
              <Switch checked={!!form.sold_out} onCheckedChange={(v) => setForm((f) => ({ ...f, sold_out: v }))} data-testid="offer-soldout" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Active (visible on page)</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="offer-active" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="save-offer-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
